import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface PortalDropdownProps {
  isOpen: boolean;
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
  className?: string;
  onClose?: () => void;
}

export const PortalDropdown: React.FC<PortalDropdownProps> = ({
  isOpen,
  children,
  triggerRef,
  className = '',
  onClose
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate position when dropdown opens
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      
      // Validate rect is valid (not all zeros or negative dimensions)
      if (rect.width === 0 || rect.height === 0 || (rect.left === 0 && rect.right === 0 && rect.top === 0 && rect.bottom === 0)) {
        // Retry after a short delay if rect is invalid
        setTimeout(() => {
          if (triggerRef.current && isOpen) {
            updatePosition();
          }
        }, 10);
        return;
      }
      
      // Calculate dropdown width (default 192px / 12rem)
      const dropdownWidth = 192;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // For fixed positioning, use viewport coordinates directly
      // getBoundingClientRect() already accounts for scroll, so we use it directly
      let left = rect.left;
      let top = rect.bottom + 4; // 4px gap
      
      // Check if dropdown would overflow right edge
      const spaceOnRight = viewportWidth - rect.right;
      if (spaceOnRight < dropdownWidth && rect.left > dropdownWidth) {
        // Align dropdown to button's right edge
        left = rect.right - dropdownWidth;
      }
      
      // Ensure dropdown doesn't go off left edge
      if (left < 0) {
        left = 8; // 8px margin from left edge
      }
      
      // Check if dropdown would overflow bottom edge
      const estimatedDropdownHeight = 200; // Approximate height
      if (top + estimatedDropdownHeight > viewportHeight) {
        // Position above button instead
        top = rect.top - estimatedDropdownHeight - 4;
        // Ensure it doesn't go off top edge
        if (top < 0) {
          top = 8;
        }
      }

      setPosition({
        top: top,
        left: left,
        width: dropdownWidth
      });
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
    updatePosition();
    });

    // Update position on scroll/resize
    const handleUpdate = () => {
      requestAnimationFrame(updatePosition);
    };
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, triggerRef]);

  // Handle clicks outside dropdown
  useEffect(() => {
    if (!isOpen) return;

    let cleanup: (() => void) | null = null;
    
    // Delay attaching click handler to prevent immediate close on open
    const timeoutId = setTimeout(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
      
      // Store cleanup function
      cleanup = () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, 50); // 50ms delay to prevent immediate close

    return () => {
      clearTimeout(timeoutId);
      if (cleanup) {
        cleanup();
      }
    };
  }, [isOpen, onClose, triggerRef]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className={`fixed bg-glass-surface/95 dark:bg-glass-surface-dark-mode/95 backdrop-blur-xl border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg shadow-xl z-[99999] max-h-80 overflow-hidden ${className}`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        position: 'fixed',
        zIndex: 99999
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export default PortalDropdown;
